import {
  elementaryV2ContentfulMappings,
  middleSchoolV2ContentfulMappings,
  digitalWellBeingV2ContentfulMappings,
  k8V2ContentfulMappings,
} from 'utils/product/contentfulMappings'
import {
  countCompleteLessons,
  countLessonsInNode,
  countTotalLessons,
  getLmsCourseTreeLookupKeys,
  normalizeLmsCourseName,
  resolveCourseTreeFromLookupKeys,
} from 'utils/lmsHelpers'

const PROGRAM_CONTENTFUL_MAPPINGS = {
  elementary: elementaryV2ContentfulMappings,
  'middle-school': middleSchoolV2ContentfulMappings,
  'digital-well-being': digitalWellBeingV2ContentfulMappings,
}

export const getClassInstanceId = classItem =>
  classItem?.instance ??
  (classItem?.registration_id != null ? String(classItem.registration_id) : '')

export const getClassTitle = classItem =>
  classItem?.title ?? classItem?.class_title ?? ''

export const getClassTier = classItem =>
  classItem?.tier ??
  (classItem?.course_name?.toLowerCase()?.includes('tier2') ? 2 : 1)

export const isClassFinished = classItem =>
  classItem?.is_finished === true ||
  classItem?.is_finished === 'true' ||
  classItem?.is_finished === 1

export const getClassCourseTreeLookupKeys = ({ classItem, program }) =>
  getLmsCourseTreeLookupKeys(classItem, program)

export const getClassTotalLessons = (classItem, courseManager, program) => {
  const lookupKeys = getClassCourseTreeLookupKeys({ classItem, program })
  const courseTrees = courseManager?.courseTrees

  if (courseTrees) {
    const courseTree = resolveCourseTreeFromLookupKeys({
      courseTrees,
      lookupKeys,
    })
    if (courseTree) return countLessonsInNode(courseTree)
  }

  if (classItem?.totalLessons > 0) return classItem.totalLessons

  return countTotalLessons(classItem, courseManager, lookupKeys)
}

export const getClassCompleteLessons = classItem => {
  if (Array.isArray(classItem?.lesson_nodes)) {
    return countCompleteLessons(classItem)
  }

  return classItem?.completeLessons ?? 0
}

const getTier1CourseLinkFromContentfulId = (courseLinks, classItem) => {
  const contentfulID = classItem?.contentfulID
  if (!contentfulID) return null

  return courseLinks?.find(link => contentfulID === link?.entryId) || null
}

/** Normalizes LMS course_name values (e.g. "5_dwb_tier-1_siteEducator" -> "5"). */
export const normalizeCourseNameForMapping = normalizeLmsCourseName

const getGradeMappingFromCourseName = ({ courseName, program }) => {
  const normalizedCourseName = normalizeCourseNameForMapping(courseName)
  if (!normalizedCourseName) return null

  const mappings = PROGRAM_CONTENTFUL_MAPPINGS[program] || []
  return (
    mappings.find(
      entry => entry?.courseName?.toLowerCase() === normalizedCourseName,
    ) || null
  )
}

const extractGradeFromCourseName = rawCourseName => {
  const normalizedCourseName = normalizeCourseNameForMapping(rawCourseName)
  if (!normalizedCourseName) return ''

  if (/^(k|\d{1,2})$/.test(normalizedCourseName)) {
    return normalizedCourseName
  }

  const gradePrefixMatch = normalizedCourseName.match(/^grade[\s-]*(k|\d{1,2})/)
  if (gradePrefixMatch?.[1]) {
    return gradePrefixMatch[1]
  }

  return ''
}

export const getClassGradeSlug = ({ classItem, courseTier1, program }) => {
  const mapping = getGradeMappingFromCourseName({
    courseName: classItem?.course_name,
    program,
  })
  if (mapping?.slug) return mapping.slug

  const slugFromCourseLink = String(courseTier1?.slug || '')
    .trim()
    .replace(/^\/+|\/+$/g, '')
  if (slugFromCourseLink) return slugFromCourseLink

  const extractedGrade = extractGradeFromCourseName(classItem?.course_name)
  if (extractedGrade) return `grade-${extractedGrade}`

  const rawCourseName = String(classItem?.course_name || '').trim()
  if (!rawCourseName) return ''

  const withoutGradePrefix = rawCourseName.replace(/^grade[\s-]*/i, '')
  return `grade-${withoutGradePrefix}`
    .toLowerCase()
    .replace(/\s+/g, '-')
    .replace(/^\/+|\/+$/g, '')
}

const getGradeDisplayTitleFromCourseName = ({ courseName, program }) =>
  getGradeMappingFromCourseName({ courseName, program })?.displayTitle || ''

const getCourseLinkForV2Class = ({ classItem, courseLinks, program }) => {
  const mapping = getGradeMappingFromCourseName({
    courseName: classItem?.course_name,
    program,
  })
  if (!mapping) return null

  return (
    courseLinks?.find(
      link =>
        link?.title === mapping.displayTitle ||
        (mapping.slug && link?.slug === mapping.slug) ||
        (mapping.id && link?.entryId === mapping.id),
    ) || null
  )
}

const getTier2ContentfulMapping = (classItem, program) => {
  if (classItem?.contentfulID) {
    return (
      k8V2ContentfulMappings?.find(
        course => classItem.contentfulID === course?.id,
      ) || {}
    )
  }

  const mappings =
    PROGRAM_CONTENTFUL_MAPPINGS[program] || k8V2ContentfulMappings
  return (
    mappings.find(
      entry =>
        entry?.courseName?.toLowerCase() ===
        String(classItem?.course_name || '').toLowerCase(),
    ) || {}
  )
}

export const getClassCardDisplayData = ({
  classItem,
  courseLinks,
  tier,
  program,
}) => {
  const courseTier1 =
    tier === 1
      ? getCourseLinkForV2Class({ classItem, courseLinks, program }) ||
        getTier1CourseLinkFromContentfulId(courseLinks, classItem)
      : null
  const { displayTitle: tier2DisplayTitle, defaultUnit } =
    tier === 2 ? getTier2ContentfulMapping(classItem, program) : {}

  const gradeDisplayTitle =
    tier === 2
      ? tier2DisplayTitle
      : getGradeDisplayTitleFromCourseName({
          courseName: classItem?.course_name,
          program,
        }) || courseTier1?.title

  const gradeSlug =
    tier === 1 ? getClassGradeSlug({ classItem, courseTier1, program }) : ''

  return {
    courseTier1,
    defaultUnit,
    gradeDisplayTitle,
    gradeSlug,
  }
}
